/* ============================================================
   ASP REAL-TIME CONFIGURATOR — production controller (v2, 2026-07-06)
   assets/asp-configurator.js  ·  vanilla JS, no framework, no build step.

   DATA MODEL (rebuilt 2026-07-06): window.ASP_CONFIG.products is keyed by
   thickness ("1","2","4"). Each thickness is ONE merged product group whose
   variants are the FULL Admin catalog for that thickness AND its Large sibling:
     "2" = standard 2" (24"-wide sizes) + Large 2" (48"/72"/96" sizes)
     "4" = standard 4" bass traps + Large 4" bass traps
   The prior island rendered from Liquid `all_products`, which is CAPPED at 250
   variants, so 215+ real 4" combos (and all Large combos) were unreachable and
   Guilford families were incomplete. The island is now the full server-baked set.

   Each variant is NORMALIZED at bake time to explicit fields:
     { id, handle, size, fabric, color, price(CENTS), available, property? }
   - Standard 1"/2": Microsuede/Burlap = discrete color. Guilford/Burch =
     placeholder fabric with color "ENTER FABRIC COLOR BELOW" + a line-item
     `property` ("Guilford Fabric Color" / "Burch Fabric Color") the buyer fills.
   - 4"/Large: every "Fabric: Color" is its own discrete variant (FR701 incl.
     White, Anchorage, Whisper all resolve to real variant ids).

   SIZE-AWARE: fabrics and colors are derived from the SELECTED SIZE, because
   the standard 24"-wide sizes and the Large sizes expose different fabric
   structures (placeholder-Guilford vs discrete-Guilford). Picking a size
   rebuilds the fabric + color lists from that size's real variants.

   PRICE TRUTH: every price is the live variant price baked server-side. On
   add-to-cart we POST the exact real variant id to /cart/add.js; a custom-color
   (1"/2" Guilford/Burch) rides its chosen color as the line-item property.
   ============================================================ */
(function () {
  'use strict';
  var root, CFG, PRODUCTS, FABRIC_COLORS, SWATCH_IMG;

  /* ---- canonical NRC (1"=0.80 Intertek, 2"=1.0, 4"=no single NRC published) ---- */
  var NRC = {
    '1': { val: '0.80', fill: 80, plain: 'Absorbs about 80% of mid and high frequency sound. Great for echo and speech clarity.' },
    '2': { val: '1.0', fill: 100, plain: 'Absorbs nearly all the sound that hits it. Studio-grade.' },
    '4': { val: 'not published', fill: 100, plain: 'Broadband absorption with the deepest low-frequency reach. No single NRC number is published for 4 inch builds.' }
  };

  /* ---- texture class by fabric family (display only) ---- */
  var TEX = {
    'Burch Prime Time': 'tex-burch', 'Microsuede': 'tex-microsuede',
    'Burlap': 'tex-burlap', 'Guilford of Maine': 'tex-guilford',
    'Guilford of Maine (CLICK G BELOW)': 'tex-guilford',
    'Guilford FR701': 'tex-guilford', 'Guilford Anchorage': 'tex-guilford', 'Guilford Whisper': 'tex-guilford'
  };

  /* ---- approximate preview hexes (only used when no swatch image exists) ---- */
  var COLOR_HEX = {
    'Black': '#1c1c1c', 'Natural / Camel': '#c8a877', 'Red': '#9e2420', 'Ivory (Off White)': '#f1ece0',
    'Ivory (Natural)': '#efe9da', 'Natural': '#d8c7a4', 'Camel': '#c8a877',
    'Blue': '#2f4d6e', 'Chocolate': '#4a342a', 'Gray': '#8a8a8a', 'Sage': '#9aa884',
    'Ivory': '#f1ece0', 'White': '#f4f4f0',
    'ENTER FABRIC COLOR BELOW': '#6f6c66'
  };

  /* ============================================================
     VARIANT INDEX — built once from the normalized injected data.
     Per thickness we index:
       byKey[size||fabric||color] -> variant
       sizes[]                    -> ordered unique sizes (standard then large)
       fabricsBySize[size]        -> ordered unique fabrics available at that size
       colorsBySizeFabric[size][fabric] -> ordered unique color names
     ============================================================ */
  function buildIndex() {
    Object.keys(PRODUCTS).forEach(function (thk) {
      var p = PRODUCTS[thk];
      p.byKey = {};
      p.sizes = [];
      p.fabricsBySize = {};
      p.colorsBySizeFabric = {};
      var seenSize = {};
      (p.variants || []).forEach(function (v) {
        // Normalized fields. Fall back to legacy option fields for safety.
        var size = v.size != null ? v.size : v.option1;
        var fabric = v.fabric != null ? v.fabric : v.option2;
        var color = v.color != null ? v.color : v.option3;
        if (size == null || fabric == null || color == null) return;
        v._size = size; v._fabric = fabric; v._color = color;
        p.byKey[key(size, fabric, color)] = v;
        if (!seenSize[size]) { seenSize[size] = 1; p.sizes.push(size); }
        if (!p.fabricsBySize[size]) p.fabricsBySize[size] = [];
        if (p.fabricsBySize[size].indexOf(fabric) === -1) p.fabricsBySize[size].push(fabric);
        if (!p.colorsBySizeFabric[size]) p.colorsBySizeFabric[size] = {};
        if (!p.colorsBySizeFabric[size][fabric]) p.colorsBySizeFabric[size][fabric] = [];
        if (p.colorsBySizeFabric[size][fabric].indexOf(color) === -1) p.colorsBySizeFabric[size][fabric].push(color);
      });
    });
  }
  function key(size, fabric, color) { return [size, fabric, color].join('||'); }

  /* current thickness product group + helpers over the selected size */
  function curProduct() { return PRODUCTS[state.thick]; }
  function curFabrics() { var p = curProduct(); return (p && p.fabricsBySize[state.size]) || []; }
  function curColors() {
    var p = curProduct(); if (!p) return [];
    return (p.colorsBySizeFabric[state.size] && p.colorsBySizeFabric[state.size][state.fabric]) || [];
  }

  /* ---- friendly fabric label (strip catalog noise) ---- */
  function fabricLabel(f) {
    return String(f).replace(' (CLICK G BELOW)', '').replace(/\s*\(.*\)\s*$/, function (m) {
      return m.indexOf('Off White') > -1 ? m : '';
    }).trim() || f;
  }
  function isGuilfordCustom(fabric, color) {
    return /Guilford of Maine/.test(fabric) && /ENTER FABRIC COLOR BELOW/i.test(color);
  }
  function isBurchCustom(fabric, color) {
    return /Burch Prime Time/.test(fabric) && /ENTER FABRIC COLOR BELOW/i.test(color);
  }
  function isCustomColor(fabric, color) {
    return isGuilfordCustom(fabric, color) || isBurchCustom(fabric, color);
  }
  // Named swatches for a placeholder fabric (Burch has 36 named colors in fabricColors).
  function namedSwatches(fabric) {
    if (!FABRIC_COLORS || !FABRIC_COLORS.length) return [];
    var out = [];
    FABRIC_COLORS.forEach(function (c) {
      if (c && c.fabric === fabric && c.name) out.push({ name: c.name, option: c.option, image: c.image });
    });
    return out;
  }
  // Any Guilford line (placeholder on 1"/2", real family on 4"/Large).
  function isGuilford(fabric) { return /Guilford/.test(String(fabric)); }
  // Guilford swatches for the 1"/2" PLACEHOLDER flow: the variant fabric
  // ("Guilford of Maine (CLICK G BELOW)") is one placeholder variant, and the buyer
  // picks the actual Guilford color which rides as a line-item property. Pull the
  // full FR701 + Anchorage + Whisper sets from fabric-color-data.json. FR701/Anchorage/
  // Whisper share some plain names, so the swatch KEY is the full option string
  // ("Guilford FR701: White") for unambiguous fulfillment; the label keeps the family.
  function guilfordSwatches() {
    if (!FABRIC_COLORS || !FABRIC_COLORS.length) return [];
    var out = [];
    FABRIC_COLORS.forEach(function (c) {
      if (!c || !c.name) return;
      if (c.fabric === 'Guilford FR701' || c.fabric === 'Guilford Anchorage' || c.fabric === 'Guilford Whisper') {
        var fam = c.fabric.replace('Guilford ', '');   // "FR701" / "Anchorage" / "Whisper"
        out.push({ name: c.option, option: c.option, image: c.image, label: fam + ' · ' + c.name, family: fam });
      }
    });
    return out;
  }
  function colorLabel(c) {
    return String(c).replace('ENTER FABRIC COLOR BELOW', 'your chosen color');
  }

  /* ---- size helpers. Sizes look like "24\" x 48\" x 2\"" (some Large 4" are
         "48\" x 48 x 4\"" with a missing inch mark; parse tolerates that). ---- */
  function sizeShort(size) {
    var m = String(size).match(/(\d+)\D+?(\d+)/);
    return m ? (m[1] + '″ × ' + m[2] + '″') : size;
  }
  function sizeInches(size) {
    var m = String(size).match(/(\d+)\D+?(\d+)/);
    return m ? [parseInt(m[1], 10), parseInt(m[2], 10)] : [24, 24];
  }
  // A size is "Large" if either dimension is >= 48 (the Large product sizes).
  function isLargeSize(size) {
    var d = sizeInches(size);
    return Math.max(d[0], d[1]) >= 48 || d[0] >= 48;
  }

  /* ============================================================ STATE ============================================================ */
  // Default: 2" panel, 24x48 (standard), Microsuede Black (a real in-catalog combo).
  var state = { thick: '2', size: null, fabric: null, color: null, customColorName: null, qty: 4 };

  function el(id) { return document.getElementById(id); }

  function ensureValidSelection() {
    var p = curProduct();
    if (!p) return;
    if (p.sizes.indexOf(state.size) === -1) {
      // Prefer a standard (non-Large) 24"-wide "x 48" size as the friendly default,
      // then any standard size, then fall back to the first size (Large-only groups).
      var standards = p.sizes.filter(function (s) { return !isLargeSize(s); });
      state.size = pick(standards, 'x 48') || standards[0] || pick(p.sizes, 'x 48') || p.sizes[0];
    }
    var fabs = curFabrics();
    if (fabs.indexOf(state.fabric) === -1) state.fabric = pick(fabs, 'Microsuede') || fabs[0];
    var cols = curColors();
    if (cols.indexOf(state.color) === -1) state.color = pick(cols, 'Black') || cols[0];
  }
  function pick(arr, hint) { for (var i = 0; i < arr.length; i++) if (String(arr[i]).indexOf(hint) > -1) return arr[i]; return null; }

  function curVariant() {
    var p = curProduct(); if (!p) return null;
    return p.byKey[key(state.size, state.fabric, state.color)] || null;
  }
  function curPriceCents() { var v = curVariant(); return v ? v.price : null; }

  /* ============================================================ RENDERERS ============================================================ */
  function buildThick() {
    var order = ['1', '2', '4'];
    var meta = { '1': ['1″', 'NRC 0.80'], '2': ['2″', 'NRC 1.0'], '4': ['4″ Bass Trap', 'Broadband + bass'] };
    el('aspThick').innerHTML = order.filter(function (t) { return PRODUCTS[t]; }).map(function (t) {
      var m = meta[t];
      return '<div class="chip ' + (t === state.thick ? 'active' : '') + '" data-thick="' + t + '">' + m[0] +
        '<span class="sub">' + m[1] + '</span></div>';
    }).join('');
  }
  function buildSize() {
    var p = curProduct();
    el('aspSize').innerHTML = p.sizes.map(function (s, i) {
      var large = isLargeSize(s);
      var sub = large ? 'large format' : (/48/.test(s) ? 'most popular' : '');
      return '<div class="chip ' + (s === state.size ? 'active' : '') + (large ? ' asp-size-large' : '') + '" data-size-idx="' + i + '">' +
        sizeShort(s) + '<span class="sub">' + sub + '</span></div>';
    }).join('');
  }
  function buildFabric() {
    var fabs = curFabrics();
    var SUBS = { 'Burch Prime Time': 'Made in USA · Class A', 'Guilford FR701': 'Class A · 44 colors',
      'Guilford Anchorage': 'Class A · 37 colors', 'Guilford Whisper': 'Class A · 24 colors',
      'Guilford of Maine (CLICK G BELOW)': 'FR701, Anchorage, Whisper · Class A', 'Microsuede': 'soft · smooth', 'Burlap': 'woven · natural' };
    el('aspFabric').innerHTML = fabs.map(function (f) {
      var sub = SUBS[f] ? '<span class="sub">' + SUBS[f] + '</span>' : '';
      return '<div class="chip ' + (f === state.fabric ? 'active' : '') + '" data-fabric="' + esc(f) + '">' +
        esc(fabricLabel(f)) + sub + '</div>';
    }).join('');
  }
  function buildColors(filter) {
    var catalogCols = curColors().slice();
    // Custom-color placeholder fabric (1"/2" Guilford or Burch): its only catalog color
    // is the "ENTER FABRIC COLOR BELOW" placeholder. Render named swatches so the buyer
    // picks a real color. The picked name is captured as state.customColorName; state.color
    // stays the placeholder so variant resolution is unchanged.
    var named;
    if (catalogCols.length === 1 && isGuilford(state.fabric) && /ENTER FABRIC COLOR BELOW/i.test(catalogCols[0])) {
      named = guilfordSwatches();
    } else if (catalogCols.length === 1 && isCustomColor(state.fabric, catalogCols[0])) {
      named = namedSwatches(state.fabric);
    } else {
      named = [];
    }
    if (named.length) {
      state.color = catalogCols[0]; // keep resolution on the placeholder variant
      var sw = named.map(function (s) {
        return { key: s.name, label: s.label || s.name, option: s.option || (state.fabric + ': ' + s.name), image: s.image };
      });
      var swKeys = sw.map(function (s) { return s.key; });
      var search = el('aspColorSearch');
      if (search) search.parentNode.style.display = (sw.length > 12) ? 'flex' : 'none';
      var shown = sw;
      if (filter) { var qn = filter.toLowerCase(); shown = sw.filter(function (s) { return s.label.toLowerCase().indexOf(qn) > -1; }); }
      if (!state.customColorName || swKeys.indexOf(state.customColorName) === -1) {
        if (!filter) state.customColorName = swKeys[0];
      }
      el('aspColorCount').textContent = shown.length + ' color' + (shown.length === 1 ? '' : 's');
      el('aspColors').innerHTML = shown.map(function (s) {
        var img = s.image || SWATCH_IMG[s.option];
        var bg = img ? 'background-image:url(' + img + ')' : 'background:#999';
        return '<div class="swatch ' + (s.key === state.customColorName ? 'active' : '') + '" data-named="' + esc(s.key) + '">' +
          '<div class="dot" style="' + bg + '"></div>' +
          '<span class="nm">' + esc(s.label) + '</span></div>';
      }).join('') || '<div class="hint">No colors match.</div>';
      renderGuilfordNotes();
      return;
    }
    renderGuilfordNotes();
    // Standard path (discrete colors: Microsuede / Burlap / 4"+Large every fabric).
    state.customColorName = null;
    var cols = catalogCols;
    var search2 = el('aspColorSearch');
    if (search2) search2.parentNode.style.display = (cols.length > 12) ? 'flex' : 'none';
    if (filter) { var q = filter.toLowerCase(); cols = cols.filter(function (c) { return c.toLowerCase().indexOf(q) > -1; }); }
    if (cols.indexOf(state.color) === -1 && !filter) state.color = cols[0];
    el('aspColorCount').textContent = cols.length + ' color' + (cols.length === 1 ? '' : 's');
    el('aspColors').innerHTML = cols.map(function (c) {
      var optStr = state.fabric + ': ' + c;          // matches fabric-color-data.json key
      var img = SWATCH_IMG[optStr] || SWATCH_IMG[c];
      var bg = img ? 'background-image:url(' + img + ')' : 'background:' + (COLOR_HEX[c] || '#999');
      var nm = c.replace(' (Off White)', '').replace('ENTER FABRIC COLOR BELOW', 'Choose color');
      return '<div class="swatch ' + (c === state.color ? 'active' : '') + '" data-color="' + esc(c) + '">' +
        '<div class="dot" style="' + bg + '"></div>' +
        '<span class="nm">' + esc(nm) + '</span></div>';
    }).join('') || '<div class="hint">No colors match.</div>';
  }
  function renderGuilfordNotes() {
    var box = el('aspGuilfordNote');
    if (!box) {
      var host = el('aspColors');
      if (host && host.parentNode) {
        box = document.createElement('div');
        box.id = 'aspGuilfordNote';
        box.className = 'asp-guilford-note';
        box.style.display = 'none';
        if (host.nextSibling) host.parentNode.insertBefore(box, host.nextSibling);
        else host.parentNode.appendChild(box);
      }
    }
    if (!box) return;
    if (!isGuilford(state.fabric)) { box.style.display = 'none'; box.innerHTML = ''; return; }
    box.style.display = 'block';
    box.innerHTML =
      '<p class="asp-guilford-min" style="margin:10px 0 6px;font-size:13px;font-weight:700;color:#232323;">' +
      'Guilford of Maine has a 6-panel minimum per order.</p>' +
      '<p class="asp-guilford-dealer" style="margin:0;font-size:13px;line-height:1.5;color:#444;">' +
      'Contact us for other Guilford options. Acoustic Sound Panels is an authorized Guilford of Maine dealer.</p>';
  }
  function updatePreview() {
    var dims = sizeInches(state.size);
    var wIn = dims[0], hIn = dims[1];
    var panel = el('aspPanel');
    var person = root.querySelector('.person');
    var personLabel = root.querySelector('.person-label');
    var PERSON_PX = 150, PERSON_IN = 68, PX_PER_IN = PERSON_PX / PERSON_IN;
    var panelW = wIn * PX_PER_IN, panelH = hIn * PX_PER_IN, personH = PERSON_PX;
    var stageH = panel.closest('.stage').clientHeight || 340;
    var usableH = stageH - 34 - 24;
    var tallest = Math.max(panelH, personH);
    if (tallest > usableH) { var k = usableH / tallest; panelW *= k; panelH *= k; personH *= k; }
    panel.style.width = panelW.toFixed(1) + 'px';
    panel.style.height = panelH.toFixed(1) + 'px';
    var previewColor = state.customColorName || state.color;
    var optStr = state.fabric + ': ' + previewColor;
    var img = SWATCH_IMG[optStr] || SWATCH_IMG[previewColor];
    if (img) { panel.style.backgroundImage = 'url(' + img + ')'; panel.style.backgroundColor = ''; }
    else { panel.style.backgroundImage = ''; panel.style.backgroundColor = COLOR_HEX[state.color] || '#777'; }
    if (person) person.style.height = personH.toFixed(1) + 'px';
    if (personLabel) personLabel.style.bottom = (34 + personH + 2).toFixed(1) + 'px';
    el('aspTex').className = 'tex ' + (TEX[state.fabric] || '');
    el('aspDims').textContent = sizeShort(state.size) + (state.thick === '4' ? ' × 4″ deep' : '');
  }
  function updateNRC() {
    var n = NRC[state.thick];
    el('aspNrcVal').textContent = 'NRC ' + n.val;
    el('aspNrcFill').style.width = n.fill + '%';
    el('aspNrcPlain').textContent = n.plain;
  }
  function updatePrice() { try { var __d = sizeInches(state.size); var __f = Math.max(__d[0],__d[1]) > 48 || Math.min(__d[0],__d[1]) > 24; var __t = __f ? 'Made in USA \u00B7 Large format ships freight. We arrange delivery with you.' : 'Made in USA \u00B7 Free ground shipping over $349.99'; var __e = document.querySelectorAll('.price-ship'); for (var __i = 0; __i < __e.length; __i++) { __e[__i].textContent = __t; } } catch (__err) {} 
    var cents = curPriceCents();
    var atc = el('aspAtc');
    if (cents == null) {
      el('aspPriceTotal').textContent = 'Not available';
      el('aspPriceEach').textContent = 'Try another fabric or size';
      atc.disabled = true; atc.textContent = 'Unavailable';
      return;
    }
    var p = cents / 100;
    atc.disabled = false; atc.textContent = 'Add to Cart';
    el('aspPriceTotal').textContent = money(p * state.qty);
    el('aspPriceEach').textContent = money(p) + ' each × ' + state.qty;
  }
  function renderAll() {
    ensureValidSelection();
    buildThick(); buildSize(); buildFabric(); buildColors(); updatePreview(); updateNRC(); updatePrice();
  }

  /* ============================================================ HELPERS ============================================================ */
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  function money(n) { return '$' + n.toFixed(2); }
  function toast(msg, isErr) {
    var t = el('aspToast'); t.textContent = msg; t.className = 'toast show' + (isErr ? ' err' : '');
    setTimeout(function () { t.className = 'toast'; }, 3600);
  }

  /* ============================================================ ADD TO CART ============================================================ */
  function addToCart() {
    var v = curVariant();
    if (!v) { toast('That combination is not available. Try another fabric or size.', true); return; }
    var atc = el('aspAtc');
    atc.classList.add('loading'); atc.disabled = true; atc.textContent = 'Adding...';

    var custom = isCustomColor(state.fabric, state.color);
    var burch = isBurchCustom(state.fabric, state.color);
    var chosenColor = custom ? null : state.color;
    if (custom) {
      chosenColor = state.customColorName;
      if (!chosenColor) {
        var ask = burch
          ? 'Enter your Burch Prime Time fabric color name:'
          : 'Enter your Guilford of Maine fabric color name (FR701, Anchorage or Whisper):';
        chosenColor = window.prompt(ask, '');
      }
      if (!chosenColor) { resetAtc(); return; }
    }
    var props = {
      'Thickness': state.thick + '"',
      'Configured': sizeShort(state.size) + ' · ' + fabricLabel(state.fabric) + ' · ' + colorLabel(chosenColor),
      'NRC': NRC[state.thick].val
    };
    if (custom) {
      // Property name comes from the baked variant (v.property) when present, else derive.
      var propName = v.property || (burch ? 'Burch Fabric Color' : 'Guilford Fabric Color');
      props[propName] = chosenColor;
    }

    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: v.id, quantity: state.qty, properties: props }] })
    }).then(function (r) { return r.json().then(function (d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function (res) {
        if (!res.ok) {
          toast((res.d && res.d.description) || 'Could not add to cart. Please try again.', true);
          resetAtc(); return;
        }
        toast('Added ' + state.qty + ' × ' + fabricLabel(state.fabric) + ' ' + colorLabel(chosenColor) + ' to your cart.');
        resetAtc();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:build'));
        setTimeout(function () { if (!document.querySelector('.cart-drawer.open, .drawer--active, [data-cart-drawer].active')) window.location.href = '/cart'; }, 600);
      }).catch(function () { toast('Network error. Please try again.', true); resetAtc(); });
  }
  function resetAtc() { var a = el('aspAtc'); a.classList.remove('loading'); a.disabled = false; updatePrice(); }

  /* ============================================================ EVENTS ============================================================ */
  var wired = false;
  function on(id, ev, fn) { var n = el(id); if (n) n.addEventListener(ev, fn); }
  function wireEvents() {
    if (wired) return; wired = true;
    on('aspThick', 'click', function (e) {
      var c = e.target.closest('[data-thick]'); if (!c) return;
      state.thick = c.dataset.thick;
      // reset size so ensureValidSelection re-picks a valid size for the new thickness
      state.size = null; state.fabric = null; state.color = null; state.customColorName = null;
      renderAll();
    });
    on('aspSize', 'click', function (e) {
      var c = e.target.closest('[data-size-idx]'); if (!c) return;
      state.size = curProduct().sizes[+c.dataset.sizeIdx];
      // fabrics/colors can differ by size; let ensureValidSelection re-validate
      renderAll();
    });
    on('aspFabric', 'click', function (e) {
      var c = e.target.closest('[data-fabric]'); if (!c) return;
      state.fabric = decode(c.dataset.fabric); state.color = null; state.customColorName = null;
      if (isGuilford(state.fabric) && state.qty < 6) { state.qty = 6; var qi = el('aspQtyInput'); if (qi) qi.value = 6; }
      renderAll();
      if (el('aspColorSearch')) el('aspColorSearch').value = '';
    });
    on('aspColors', 'click', function (e) {
      var named = e.target.closest('[data-named]');
      if (named) { state.customColorName = decode(named.dataset.named); buildColors(el('aspColorSearch') ? el('aspColorSearch').value : ''); updatePreview(); updatePrice(); return; }
      var c = e.target.closest('[data-color]'); if (!c) return; state.color = decode(c.dataset.color); buildColors(el('aspColorSearch') ? el('aspColorSearch').value : ''); updatePreview(); updatePrice();
    });
    on('aspColorSearch', 'input', function (e) { buildColors(e.target.value); });
    on('aspQtyMinus', 'click', function () { state.qty = Math.max(1, state.qty - 1); el('aspQtyInput').value = state.qty; updatePrice(); });
    on('aspQtyPlus', 'click', function () { state.qty = state.qty + 1; el('aspQtyInput').value = state.qty; updatePrice(); });
    on('aspQtyInput', 'change', function (e) { var v = parseInt(e.target.value, 10); if (isNaN(v) || v < 1) v = 1; state.qty = v; e.target.value = v; updatePrice(); });
    on('aspAtc', 'click', addToCart);
  }
  function decode(s) { var t = document.createElement('textarea'); t.innerHTML = s; return t.value; }

  /* ============================================================ BOOT ============================================================
     The island is the full server-baked catalog (all thicknesses + Large siblings),
     so there is NO per-product /products/{handle}.js refresh anymore: the .js endpoint
     is itself capped at 250 variants and would REINTRODUCE the truncation this rebuild
     fixes. The baked data is authoritative and complete.
     ------------------------------------------------------------ */
  function bootRender() {
    FABRIC_COLORS = CFG.fabricColors || [];
    SWATCH_IMG = {};
    FABRIC_COLORS.forEach(function (c) { if (c && c.option) SWATCH_IMG[c.option] = c.image; });
    buildIndex();
    wireEvents();
    var fb = document.querySelector('.asp-native-fallback'); if (fb) fb.style.display = 'none';
    root.style.display = 'block';

    // Portal the fixed-position UI to <body>. The theme wraps page sections in
    // transformed ancestors, and CSS position:fixed re-anchors to the nearest
    // transformed ancestor - so the pricebar (price + Add to Cart) rendered at
    // the bottom of the page FLOW, invisible while configuring (2026-07-06:
    // nobody could see prices). Moving the nodes to <body> restores true
    // viewport-fixed behavior; all JS lookups are getElementById so they
    // survive the move. Styles for the moved nodes live in the .asp-portal
    // rules appended to asp-configurator.css.
    var bar = document.querySelector('#asp-configurator .pricebar') || document.querySelector('.pricebar');
    var tst = el('aspToast');
    [bar, tst].forEach(function (n) {
      if (n && n.parentNode !== document.body) { n.classList.add('asp-portal'); document.body.appendChild(n); }
    });
    renderAll();
  }
  // Decode the compact index-encoded island (v:2). To fit Shopify's 256KB template
  // limit, the baked island encodes each thickness as string tables + integer-tuple
  // rows: [id, sizeIdx, fabricIdx, colorIdx, priceCents, handleIdx]. We expand it back
  // to the { variants:[{id,handle,size,fabric,color,price,available,property}] } shape
  // buildIndex() consumes. A legacy (uncompacted) island passes through unchanged.
  function decodeProducts(cfg) {
    var src = (cfg && cfg.products) || {};
    if (cfg && cfg.v !== 2) return src; // legacy shape: already {thk:{variants:[...]}}
    var out = {};
    Object.keys(src).forEach(function (thk) {
      var p = src[thk] || {};
      var sizes = p.sizes || [], fabrics = p.fabrics || [], colors = p.colors || [],
          handles = p.handles || [], rows = p.rows || [], propMap = p.props || {};
      var variants = rows.map(function (r) {
        var fi = r[2];
        var v = {
          id: r[0], size: sizes[r[1]], fabric: fabrics[fi], color: colors[r[3]],
          price: r[4], handle: handles[r[5]] || handles[0], available: true
        };
        // Attach the fulfillment property name for custom-color placeholder fabrics.
        if (v.color === 'ENTER FABRIC COLOR BELOW' && propMap[fi]) v.property = propMap[fi];
        return v;
      });
      out[thk] = { handles: handles, variants: variants };
    });
    return out;
  }
  var booted = false;
  function boot() {
    root = document.getElementById('asp-configurator');
    if (!root || !window.ASP_CONFIG) return;
    if (booted) return; booted = true;
    CFG = window.ASP_CONFIG;
    PRODUCTS = decodeProducts(CFG);
    bootRender();
  }
  window.__aspBoot = boot;
  // Late-arriving fabric-color data (swatch images + named Guilford/Burch swatches):
  // the inline boot script fetches fabric-color-data.json asynchronously and hands it
  // here whenever it lands. We update the swatch-image index and re-render the color
  // grid in place. Prices/ATC never depend on this, so boot is never gated by it.
  window.__aspSetFabricColors = function (colors) {
    if (!Array.isArray(colors) || !colors.length) return;
    FABRIC_COLORS = colors;
    if (CFG) CFG.fabricColors = colors;
    SWATCH_IMG = {};
    FABRIC_COLORS.forEach(function (c) { if (c && c.option) SWATCH_IMG[c.option] = c.image; });
    if (booted && root) { buildColors(el('aspColorSearch') ? el('aspColorSearch').value : ''); updatePreview(); }
  };
  if (document.readyState !== 'loading') { setTimeout(function () { if (!root) boot(); }, 0); }
  else document.addEventListener('DOMContentLoaded', function () { setTimeout(function () { if (!root) boot(); }, 50); });
})();

